// Runbook editor — the practice's portal runbook, as versions.
//
// A runbook has two halves: prose a person reads, and a structured JSON block the
// skill executes. Saving ALWAYS appends a new version (A's runbook_service never
// edits one in place), and a new version starts unapproved: approval is per
// version, by an admin, and never travels with a save.
//
// Approval matters for one thing: an unapproved runbook lets the skill read
// through a captured portal endpoint; only an approved one lets it submit through
// one. The banner says so, because "approve" otherwise reads like "looks fine".

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  approveRunbook,
  fetchRunbook,
  saveRunbookVersion,
  unapproveRunbook,
  uploadRunbook,
} from "./api";
import { relativeTime } from "./ageing";
import { invalidatePmsGates } from "./usePmsGates";
import "./Queue.css";

const API_URL = process.env.REACT_APP_API_URL || "";

// Where "Attach a new practice management system" goes: the read-side kit in
// atlas-billing-ops. The URL is config (GET /api/config, `pmsKitUrl`), fetched once
// per mount; the words are fixed here. The kit works with any capable coding agent;
// Claude Cowork is what we test with.
export const ATTACH_TEXT =
  "Attach a new practice management system — the kit works with any capable coding agent; Claude Cowork is what we test with.";

export async function fetchKitUrl() {
  const response = await fetch(`${API_URL}/api/config`, {
    credentials: "include",
  });
  if (!response.ok) throw new Error("Could not load the kit link.");
  const body = await response.json().catch(() => null);
  return body && typeof body.pmsKitUrl === "string" ? body.pmsKitUrl : null;
}

// The versions table's "UNKNOWN / coverage" cell: a v2's contract coverage, anything
// else's UNKNOWN count. The server's summary when it sent one; else counted here, as
// before brief 15.
export function versionCoverageText(rb) {
  const s = rb.summary;
  if (s && s.schema === "v2" && s.coverage) {
    const c = s.coverage;
    return `${c.available} of ${c.of} operations · ${c.complete.length} feature${
      c.complete.length === 1 ? "" : "s"
    } complete${s.liveReady === false ? " · not live-ready" : ""}`;
  }
  const n = s ? s.unknownCount : unknownCountOf(rb.structured);
  return n === 0 ? "no UNKNOWN" : `${n} UNKNOWN`;
}

export function versionSchemaText(rb) {
  if (rb.summary && rb.summary.schema) return rb.summary.schema;
  const schema = rb.structured && rb.structured.schema;
  return typeof schema === "number"
    ? `v${schema}`
    : rb.structured
      ? "v1/v8"
      : "—";
}

const CHANNEL_LABEL = {
  portal_ui: "Portal form (portal_ui)",
  portal_api: "Captured portal endpoint (portal_api)",
};

const pretty = (structured) =>
  structured ? JSON.stringify(structured, null, 2) : "";

function versionText(rb) {
  if (!rb) return "";
  return [rb.prose || "", pretty(rb.structured), rb.submitChannel || ""].join(
    "\n",
  );
}

// "Changed since previous", as line counts: lines added and lines removed,
// compared as multisets. Not a real diff — enough to see "one URL changed" versus
// "rewritten".
export function changedLines(previous, current) {
  const count = (text) => {
    const map = new Map();
    text.split("\n").forEach((line) => map.set(line, (map.get(line) || 0) + 1));
    return map;
  };
  const before = count(previous);
  const after = count(current);
  let added = 0;
  let removed = 0;
  after.forEach((n, line) => {
    added += Math.max(0, n - (before.get(line) || 0));
  });
  before.forEach((n, line) => {
    removed += Math.max(0, n - (after.get(line) || 0));
  });
  return { added, removed };
}

// Parse the raw JSON box: empty means "no structured half"; anything else must be
// a JSON object, the same rule A's append_version enforces.
export function parseStructured(text) {
  const trimmed = (text || "").trim();
  if (!trimmed) return { ok: true, value: null };
  try {
    const value = JSON.parse(trimmed);
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return {
        ok: false,
        error: "The structured half must be a JSON object ({…}).",
      };
    }
    return { ok: true, value };
  } catch (err) {
    return { ok: false, error: `Not valid JSON: ${err.message}` };
  }
}

const unknownCountOf = (structured) =>
  (pretty(structured).match(/UNKNOWN/g) || []).length;

const unknownCount = (rb) =>
  rb.summary ? rb.summary.unknownCount : unknownCountOf(rb.structured);

export default function RunbookEditor({
  practiceId,
  isAdmin = false,
  // Upload is a super admin's act: a file from outside Atlas becomes the version
  // the skill will read. Admins approve; super admins also upload.
  isSuperAdmin = false,
  readOnly = false,
  // False while the editor is mounted but its tab is hidden. Reopening the tab
  // re-reads the lineage (unless there are unsaved edits), so the "latest" and the
  // approval badge an admin acts on are never a stale snapshot.
  active = true,
}) {
  const [meta, setMeta] = useState(null);
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [viewingId, setViewingId] = useState(null);
  const [prose, setProse] = useState("");
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState("");
  const [submitChannel, setSubmitChannel] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmingApprove, setConfirmingApprove] = useState(false);
  const [approving, setApproving] = useState(false);
  const [unapproving, setUnapproving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // The 422's list: every problem the server found, shown under the error line.
  const [problems, setProblems] = useState([]);
  const [kitUrl, setKitUrl] = useState(null);
  const fileRef = useRef(null);
  // Set when Save found a newer version than the one the draft started from. Save
  // stays refused until Discard reloads the latest, so a second click cannot save
  // the stale draft over it.
  const [staleBy, setStaleBy] = useState(null);

  const ticket = useRef(0);

  const resetDraft = useCallback((latest) => {
    setProse(latest ? latest.prose : "");
    setRawText(latest ? pretty(latest.structured) : "");
    setSubmitChannel(latest?.submitChannel || "");
    setRawMode(false);
    setViewingId(null);
    setConfirmingApprove(false);
    setStaleBy(null);
  }, []);

  const load = useCallback(
    async (wantedPortal) => {
      if (!practiceId) return;
      const mine = (ticket.current += 1);
      setLoading(true);
      setError(null);
      try {
        const body = await fetchRunbook(practiceId, wantedPortal);
        if (mine !== ticket.current) return;
        setMeta(body);
        setPortal(body.portal);
        resetDraft(body.latest);
      } catch (err) {
        if (mine !== ticket.current) return;
        setError(err.message);
        setMeta(null);
      } finally {
        if (mine === ticket.current) setLoading(false);
      }
    },
    [practiceId, resetDraft],
  );

  useEffect(() => {
    setMessage(null);
    setProblems([]);
    load(undefined);
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchKitUrl()
      .then((url) => {
        if (!cancelled) setKitUrl(url);
      })
      .catch(() => {
        if (!cancelled) setKitUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const latest = meta?.latest || null;
  const history = useMemo(() => meta?.history || [], [meta]);
  const viewing = viewingId
    ? history.find((rb) => rb.id === viewingId)
    : latest;
  const isLatest = !viewingId || viewingId === latest?.id;
  const canEdit = !readOnly && isLatest && !!latest;

  const parsed = parseStructured(rawText);
  const dirty =
    !!latest &&
    (prose !== latest.prose ||
      (rawMode && rawText.trim() !== pretty(latest.structured).trim()) ||
      (submitChannel || null) !== (latest.submitChannel || null));
  const canSave = canEdit && dirty && parsed.ok && !saving && !staleBy;

  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const portalRef = useRef(portal);
  portalRef.current = portal;
  const wasActive = useRef(active);
  useEffect(() => {
    if (active && !wasActive.current && !dirtyRef.current) {
      load(portalRef.current || undefined);
    }
    wasActive.current = active;
  }, [active, load]);

  const diffs = useMemo(() => {
    const out = {};
    history.forEach((rb, i) => {
      const previous = history[i + 1];
      out[rb.id] = previous
        ? changedLines(versionText(previous), versionText(rb))
        : null;
    });
    return out;
  }, [history]);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      // Refuse to save over a version we have not seen. Versions are append-only,
      // so saving from a stale base would quietly drop someone else's change —
      // most often the skill's own write-back — from the latest version.
      const current = await fetchRunbook(practiceId, portal);
      // `latest` here is still the version the draft started from: the lineage is
      // only replaced by load(), which also resets the draft.
      if (current.latest && current.latest.id !== latest.id) {
        setStaleBy({
          version: current.latest.version,
          author: current.latest.author,
        });
        setError(
          `Version ${current.latest.version} was saved by ${current.latest.author || "someone"} while you were editing version ${latest.version}. ` +
            "Your edits are still here and were not saved: copy them, press Discard to load the latest version, then reapply them.",
        );
        return;
      }
      const saved = await saveRunbookVersion(practiceId, {
        portal,
        prose,
        structured: rawMode ? parsed.value : latest.structured,
        submitChannel,
      });
      setMessage({
        type: "success",
        text: `Saved version ${saved?.version ?? ""}. It is unapproved until an admin approves it.`,
      });
      await load(portal);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!latest || approving) return;
    setApproving(true);
    setError(null);
    setMessage(null);
    try {
      const approved = await approveRunbook(practiceId, latest.id);
      setMessage({
        type: "success",
        text: `Version ${approved?.version ?? latest.version} approved.`,
      });
      invalidatePmsGates(practiceId);
      await load(portal);
    } catch (err) {
      setError(err.message);
      setConfirmingApprove(false);
    } finally {
      setApproving(false);
    }
  };

  const unapprove = async () => {
    if (!latest || unapproving) return;
    setUnapproving(true);
    setError(null);
    setMessage(null);
    try {
      const row = await unapproveRunbook(practiceId, latest.id);
      setMessage({
        type: "success",
        text: `Approval of version ${row?.version ?? latest.version} withdrawn.`,
      });
      invalidatePmsGates(practiceId);
      await load(portal);
    } catch (err) {
      setError(err.message);
    } finally {
      setUnapproving(false);
    }
  };

  // Upload a runbook file (v1/v8 or v2) as the next version of this practice's
  // portal. A v2 is validated by the server first: a 422 lists every problem and
  // nothing is saved. Always a NEW, unapproved version, like a save.
  const upload = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    setError(null);
    setProblems([]);
    setMessage(null);
    try {
      const row = await uploadRunbook(practiceId, file, {
        portal: portal || undefined,
      });
      const summary = row?.summary;
      const what = summary
        ? summary.schema === "v2" && summary.coverage
          ? ` (${summary.schema}: ${summary.coverage.available} of ${summary.coverage.of} operations)`
          : ` (${summary.schema || "prose"}, ${summary.unknownCount} UNKNOWN)`
        : "";
      setMessage({
        type: "success",
        text: `Uploaded ${file.name} as version ${row?.version ?? ""}${what}. It is unapproved until an admin approves it.`,
      });
      invalidatePmsGates(practiceId);
      await load(row?.portalKey || portal || undefined);
    } catch (err) {
      setError(err.message);
      setProblems(err.problems || []);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const changePortal = (next) => {
    setMessage(null);
    load(next || undefined);
  };

  return (
    <div className="sq-runbook">
      <div className="sq-run-banner grey">
        <span>
          Unapproved runbooks let the skill read; approval lets it submit
          through a captured endpoint.
        </span>
      </div>

      {message && (
        <div className={`sq-message ${message.type}`}>
          <span>{message.text}</span>
          <button
            className="sq-message-close"
            aria-label="Dismiss"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}
      {error && (
        <div className="sq-message error" role="alert">
          <span>
            {error}
            {problems.length > 0 && (
              <ul className="sq-rb-problems" data-testid="upload-problems">
                {problems.map((problem, i) => (
                  <li key={i}>{problem}</li>
                ))}
              </ul>
            )}
          </span>
          <button
            className="sq-message-close"
            aria-label="Dismiss"
            onClick={() => {
              setError(null);
              setProblems([]);
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Upload: a super admin's act, offered whether or not a runbook exists yet —
          the first version of a new portal arrives this way as often as from a
          skill run. The file input is hidden behind the button, as a form control
          in a toolbar would be. */}
      {isSuperAdmin && !readOnly && (
        <div className="sq-tab-toolbar">
          <span className="sq-rb-upload">
            <input
              ref={fileRef}
              type="file"
              accept=".md,.markdown,.json,.txt,text/markdown,application/json,text/plain"
              style={{ display: "none" }}
              aria-label="Runbook file"
              data-testid="runbook-file"
              onChange={(e) => upload(e.target.files && e.target.files[0])}
              disabled={uploading || saving}
            />
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={uploading || saving || loading}
              title="A v1/v8 or v2 runbook file (.md with one json runbook block, or bare JSON) becomes the next version"
            >
              {uploading ? "Uploading…" : "Upload runbook file"}
            </button>
            <span className="sq-muted">
              v1/v8 or v2; a v2 is checked against the live-run rules first.
            </span>
          </span>
        </div>
      )}

      {loading && !meta && <p className="sq-empty">Loading the runbook…</p>}

      {meta && meta.portals.length > 1 && (
        <label className="sq-inline-field">
          Portal
          <select
            value={portal || ""}
            onChange={(e) => changePortal(e.target.value)}
            disabled={loading || saving || dirty}
            title={dirty ? "Save or discard your edits first" : undefined}
          >
            {!portal && <option value="">Choose a portal…</option>}
            {meta.portals.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      )}

      {meta && meta.portals.length === 0 && (
        <p className="sq-empty">
          No runbook yet — the first skill run writes one.
        </p>
      )}

      {meta && latest && viewing && (
        <>
          <div className="sq-rb-head">
            <div>
              <strong>
                {portal} · version {viewing.version}
              </strong>
              {viewing.approvedBy ? (
                <span className="sq-status sq-status-submitted sq-rb-badge">
                  Approved by {viewing.approvedBy}{" "}
                  {viewing.approvedAt ? relativeTime(viewing.approvedAt) : ""}
                </span>
              ) : (
                <span className="sq-status sq-status-removed sq-rb-badge">
                  Unapproved
                </span>
              )}
              {unknownCount(viewing) > 0 && (
                <span className="sq-age-chip amber sq-rb-badge">
                  {unknownCount(viewing)} UNKNOWN — live runs refuse this
                  version
                </span>
              )}
              {viewing.summary && viewing.summary.schema === "v2" && (
                <span
                  className={`sq-status sq-rb-badge ${
                    viewing.summary.liveReady
                      ? "sq-status-submitted"
                      : "sq-cov-partial"
                  }`}
                  title={
                    viewing.summary.liveReady
                      ? "A live run accepts this version"
                      : (viewing.summary.problems || []).join("\n")
                  }
                >
                  v2 · {versionCoverageText(viewing)}
                </span>
              )}
              <div className="sq-muted">
                by {viewing.author || "—"}
                {viewing.createdAt
                  ? ` · ${relativeTime(viewing.createdAt)}`
                  : ""}
                {viewing.submitChannel
                  ? ` · submits via ${CHANNEL_LABEL[viewing.submitChannel] || viewing.submitChannel}`
                  : ""}
              </div>
            </div>
            <div className="sq-rb-actions">
              {!isLatest && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setViewingId(null)}
                >
                  Back to latest (v{latest.version})
                </button>
              )}
              {isLatest &&
                isAdmin &&
                !readOnly &&
                !latest.approvedBy &&
                !confirmingApprove && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setConfirmingApprove(true)}
                    disabled={dirty || saving}
                    title={
                      dirty ? "Save or discard your edits first" : undefined
                    }
                  >
                    Approve version {latest.version}
                  </button>
                )}
              {isLatest && isAdmin && !readOnly && !!latest.approvedBy && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={unapprove}
                  disabled={dirty || saving || unapproving}
                  title="Withdraw the approval: the bundle stops handing this version out and a live run refuses it"
                >
                  {unapproving
                    ? "Withdrawing…"
                    : `Withdraw approval of version ${latest.version}`}
                </button>
              )}
              {isLatest && confirmingApprove && (
                <span className="sq-rb-confirm">
                  Approve version {latest.version} as read?
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setConfirmingApprove(false)}
                    disabled={approving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={approve}
                    disabled={approving}
                  >
                    {approving ? "Approving…" : "Approve"}
                  </button>
                </span>
              )}
            </div>
          </div>

          {!isLatest && (
            <p className="sq-muted sq-rb-note">
              Viewing an older version, read-only. Edits always start from the
              latest version.
            </p>
          )}

          <div className="sq-rb-panes">
            <div className="sq-rb-pane">
              <div className="sq-rb-pane-title">Prose</div>
              {canEdit ? (
                <textarea
                  className="sq-rb-text"
                  value={prose}
                  onChange={(e) => setProse(e.target.value)}
                  disabled={saving}
                  aria-label="Runbook prose"
                />
              ) : (
                <pre className="sq-rb-pre">{viewing.prose || "—"}</pre>
              )}
            </div>
            <div className="sq-rb-pane">
              <div className="sq-rb-pane-title">
                Structured
                {canEdit && (
                  <button
                    className="sq-link sq-rb-toggle"
                    onClick={() => {
                      if (rawMode) setRawText(pretty(latest.structured));
                      setRawMode(!rawMode);
                    }}
                    disabled={saving}
                  >
                    {rawMode ? "Discard raw edits" : "Edit raw JSON"}
                  </button>
                )}
              </div>
              {canEdit && rawMode ? (
                <>
                  <textarea
                    className="sq-rb-text sq-rb-mono"
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                    disabled={saving}
                    spellCheck={false}
                    aria-label="Structured runbook JSON"
                  />
                  {!parsed.ok && (
                    <div className="sq-message error sq-rb-json-error">
                      {parsed.error}
                    </div>
                  )}
                </>
              ) : (
                <pre className="sq-rb-pre sq-rb-mono">
                  {pretty(viewing.structured) || "—"}
                </pre>
              )}
            </div>
          </div>

          {canEdit && (
            <div className="sq-rb-save">
              <label className="sq-inline-field">
                Submits via
                <select
                  value={submitChannel}
                  onChange={(e) => setSubmitChannel(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Not set</option>
                  <option value="portal_ui">{CHANNEL_LABEL.portal_ui}</option>
                  <option value="portal_api">{CHANNEL_LABEL.portal_api}</option>
                </select>
              </label>
              <span className="sq-muted">
                Saving creates version {latest.version + 1}, unapproved.
              </span>
              {(dirty || staleBy) && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() =>
                    staleBy ? load(portal || undefined) : resetDraft(latest)
                  }
                  disabled={saving}
                >
                  {staleBy ? "Discard and load the latest" : "Discard"}
                </button>
              )}
              <button
                className="btn btn-primary btn-sm"
                onClick={save}
                disabled={!canSave}
              >
                {saving ? "Saving…" : "Save as new version"}
              </button>
            </div>
          )}

          <h3 className="sq-section-title">Version history</h3>
          <div className="sq-table-wrap">
            <table className="sq-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Schema</th>
                  <th>UNKNOWN / coverage</th>
                  <th>Author</th>
                  <th>Saved</th>
                  <th>Changed since previous</th>
                  <th>Approval</th>
                </tr>
              </thead>
              <tbody>
                {history.map((rb) => {
                  const d = diffs[rb.id];
                  const selected = viewing && viewing.id === rb.id;
                  return (
                    <tr
                      key={rb.id}
                      className={selected ? "sq-rb-selected" : ""}
                    >
                      <td>
                        <button
                          className="sq-link sq-rb-version"
                          onClick={() =>
                            setViewingId(rb.id === latest.id ? null : rb.id)
                          }
                          disabled={dirty && rb.id !== latest.id}
                          title={
                            dirty && rb.id !== latest.id
                              ? "Save or discard your edits first"
                              : undefined
                          }
                        >
                          v{rb.version}
                        </button>
                        {rb.id === latest.id && (
                          <span className="sq-muted"> latest</span>
                        )}
                      </td>
                      <td className="sq-nowrap">{versionSchemaText(rb)}</td>
                      <td>{versionCoverageText(rb)}</td>
                      <td>{rb.author || "—"}</td>
                      <td className="sq-nowrap">
                        {rb.createdAt ? relativeTime(rb.createdAt) : "—"}
                      </td>
                      <td className="sq-nowrap">
                        {d
                          ? `+${d.added} / −${d.removed} lines`
                          : "first version"}
                      </td>
                      <td>
                        {rb.approvedBy ? (
                          <span className="sq-status sq-status-submitted">
                            {rb.approvedBy}
                          </span>
                        ) : (
                          <span className="sq-muted">unapproved</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {kitUrl && (
        <p className="sq-rb-attach">
          <a href={kitUrl} target="_blank" rel="noopener noreferrer">
            {ATTACH_TEXT}
          </a>
        </p>
      )}
    </div>
  );
}
